import React from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import { Box, Button, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';

export const LENS_EDIT_BAR_HEIGHT = 40;

interface LensEditBarProps {
  hasUnsavedChanges: boolean;
  isSaving?: boolean;
  hasConflict?: boolean;
  onDiscard: () => void;
  onDone: () => void;
}

/**
 * The one cue that the board is in edit mode: an Ocean Teal bar over the board column, above
 * the Lens title. It names the mode, says whether a draft is waiting, and holds Discard and
 * Done editing. The chat pane carries no edit chrome of its own.
 */
export default function LensEditBar({
  hasUnsavedChanges,
  isSaving = false,
  hasConflict = false,
  onDiscard,
  onDone,
}: LensEditBarProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const onBar = tokens.accentContrast;
  const status = isSaving
    ? 'Saving…'
    : hasConflict
      ? 'Changes need review'
      : hasUnsavedChanges
        ? 'Draft · unsaved changes'
        : 'No unsaved changes';
  return (
    <Stack
      alignItems="center"
      direction="row"
      spacing={{ xs: 0.75, sm: 1.5 }}
      data-testid="lens-edit-bar"
      sx={{
        bgcolor: tokens.band,
        color: onBar,
        height: LENS_EDIT_BAR_HEIGHT,
        px: { sm: 3, xs: 1.5 },
        flexShrink: 0,
      }}
    >
      <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: 'inherit', whiteSpace: 'nowrap' }}>Editing</Typography>
      <Typography
        role="status"
        sx={{ fontSize: 13.5, color: alpha(onBar, 0.78), whiteSpace: 'nowrap', minWidth: 0 }}
        noWrap
      >
        {status}
      </Typography>
      <Box sx={{ flex: 1 }} />
      <Button
        disabled={isSaving || (!hasUnsavedChanges && !hasConflict)}
        onClick={onDiscard}
        size="small"
        sx={{
          color: onBar,
          borderColor: alpha(onBar, 0.55),
          minHeight: 28,
          minWidth: 'auto',
          flexShrink: 0,
          textTransform: 'none',
          '&.Mui-disabled': { color: alpha(onBar, 0.4), borderColor: alpha(onBar, 0.25) },
        }}
        variant="outlined"
      >
        Discard
      </Button>
      <Button
        disabled={isSaving}
        onClick={onDone}
        size="small"
        sx={{
          bgcolor: onBar,
          color: tokens.band,
          minHeight: 28,
          minWidth: 'auto',
          flexShrink: 0,
          textTransform: 'none',
          fontWeight: 600,
          '&:hover': { bgcolor: alpha(onBar, 0.9) },
          '&.Mui-disabled': { bgcolor: alpha(onBar, 0.35), color: alpha(onBar, 0.6) },
        }}
        variant="contained"
      >
        Done editing
      </Button>
    </Stack>
  );
}
